<script setup lang="ts">
import { computed } from 'vue'
import type { ReportSection, ReportSelection } from '../reports'

const props = defineProps<{ section: ReportSection; disabled: boolean }>()
const emit = defineEmits<{ select: [value: ReportSelection] }>()

const headers = computed(() => props.section.columns.map(column => {
  const numeric = props.section.metrics.includes(column.key) || column.key === 'completion_time'

  return {
    ...column,
    sortable: false,
    align: numeric ? 'end' as const : 'start' as const,
    cellProps: { class: numeric ? 'report-metrics__numeric' : undefined },
  }
}))
</script>

<template>
  <VCard variant="outlined" class="report-metrics">
    <VCardTitle class="report-metrics__title">
      {{ section.title }}
    </VCardTitle>
    <VDataTable
      :headers="headers"
      :items="section.rows"
      :items-per-page="10"
      :hide-default-footer="section.rows.length <= 10"
      :items-per-page-options="[10, 25, 50]"
      items-per-page-text="Строк на странице"
      no-data-text="Нет данных"
      class="report-metrics__table"
    >
      <template v-for="metric in section.metrics" :key="metric" #[`item.${metric}`]="{ item }">
        <VBtn
          v-if="Number(item[metric]) > 0"
          variant="text"
          size="small"
          class="report-metrics__value"
          :disabled="disabled"
          :aria-label="`${item.label}: ${section.columns.find(c => c.key === metric)?.title} ${item[metric]}. Показать записи`"
          @click="emit('select', { section: section.key, bucket: item.key, metric })"
        >
          {{ item[metric] }}
        </VBtn>
        <span v-else class="report-metrics__value text-disabled">0</span>
      </template>
    </VDataTable>
  </VCard>
</template>

<style scoped lang="scss">
.report-metrics {
  min-inline-size: 0;
  &__title { font-size: 1rem; line-height: 1.5; white-space: normal; overflow-wrap: anywhere; }
  &__table :deep(th) { background: rgba(var(--v-theme-primary), .12); }
  &__table :deep(td:first-child) { min-inline-size: 180px; }
  &__table :deep(.report-metrics__numeric) {
    font-variant-numeric: lining-nums tabular-nums;
    white-space: nowrap;
  }
  &__value {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    min-inline-size: 36px;
    margin-inline-end: -8px;
    padding-inline: 8px;
    font: inherit;
    letter-spacing: normal;
  }
  @media (max-width: 600px) {
    &__table :deep(td:first-child) { min-inline-size: 120px; }
    &__table :deep(td), &__table :deep(th) { padding-inline: 8px !important; }
  }
}
</style>
